import {
    ColDef,
    ColumnState,
    DefaultMenuItem,
    GetMainMenuItems,
    GetMainMenuItemsParams,
    GridApi,
    IRowNode,
    MenuItemDef,
    RowHeightParams,
} from "ag-grid-community";
import { isAddonField } from "common/src/models/Field";
import { memoize } from "lodash";
import { Experience, FieldResponse, Timeslot } from "types";
import { defaultColumnDefsOrder } from "../Roster/RosterGuestList/helpers";
import { useDashboardTableStore } from "./useDashboardTableStore";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardGroupBy } from "@/types/Dashboard";
import { getResource } from "@/utils/getCollection";

export const DEFAULT_ROW_HEIGHT = 42;
export const SUMMARY_ROW_HEIGHT = 55;

export const onColumnPinned = (columnApi: GridApi, maxPinnedColumns: number) => {
    if (!columnApi) return;
    const pinnedColumns = columnApi.getAllGridColumns().filter((col) => col.isPinned());
    const allColumns = columnApi.getAllDisplayedColumns();
    if (pinnedColumns.length >= maxPinnedColumns) {
        const newState = allColumns.map((col) => ({
            colId: col.getColId(),
            lockPinned: col.isPinned() ? false : true,
        }));
        columnApi.applyColumnState({ state: newState });
    } else {
        const newState = allColumns.map((col) => ({
            colId: col.getColId(),
            lockPinned: false,
        }));
        columnApi.applyColumnState({ state: newState });
    }
};

export const handleColumnPinAction = (columnApi: GridApi, colId: string, pinned: "left" | "right" | null) => {
    // Handle column reorder when groupBy is applied, no need to update store state in this case
    columnApi?.applyColumnState({
        state: [{ colId, pinned }],
        applyOrder: true,
    });

    if (useDashboardFilterStore.getState().filters.groupBy === DashboardGroupBy.NONE) {
        useDashboardTableStore.getState().actions.setColumnState(columnApi.getColumnState());
    }
};

export const insertAtOriginalIndex = (columns: ColDef[], colId: string, element: ColDef) => {
    const origIndex = defaultColumnDefsOrder.findIndex((c) => c.colId === colId);
    if (origIndex >= 0 && origIndex <= columns.length) {
        const updated = [...columns];
        updated.splice(origIndex, 0, element);
        return updated;
    }
    // fallback if colId not in defaultColumnDefsOrder
    return [...columns, element];
};

export const createMainMenuItems = (
    groupLabel: string,
    gridReferences: Record<string, { api: GridApi }>,
): GetMainMenuItems => {
    return (params: GetMainMenuItemsParams): (MenuItemDef | DefaultMenuItem)[] => {
        const column = params.column;
        if (!column) return [];

        const colId = column.getColId();
        const gridRef = gridReferences[groupLabel];
        if (!gridRef) return [];

        return [
            {
                name: "Pin Left",
                action: () => handleColumnPinAction(gridRef.api, colId, "left"),
            },
            {
                name: "Pin Right",
                action: () => handleColumnPinAction(gridRef.api, colId, "right"),
            },
            {
                name: "No Pin",
                action: () => handleColumnPinAction(gridRef.api, colId, null),
            },
        ];
    };
};

export const setTableHeight = (gridApi: any, container: HTMLElement | null) => {
    if (!gridApi || !container) return;

    const rowNodes: IRowNode[] = [];
    gridApi.forEachNode((node: IRowNode) => {
        if (node.displayed) rowNodes.push(node);
    });

    const totalRowHeight = rowNodes.reduce((sum, node) => sum + (node.rowHeight ?? 0), 0);
    const headerHeight = 60;
    const totalHeight = totalRowHeight + headerHeight;

    const maxHeight = window.innerHeight - 245;
    container.style.height = `${Math.min(totalHeight, maxHeight)}px`;
};

export const getMemoizedExperience = memoize((productId: string) => getResource<Experience>("products", productId));

export const getExperiencesForTimeslots = (timeslots: Timeslot[]): Experience[] => {
    return timeslots
        .map((timeslot) => getMemoizedExperience(timeslot.product))
        .filter((experience): experience is Experience => !!experience);
};

export const applyFlexToLastVisible = (state: Array<ColumnState>): Array<ColumnState> => {
    let lastVisibleId: string | undefined;
    for (const s of state) {
        if (!s.hide) lastVisibleId = s.colId; // track last seen visible
    }
    if (!lastVisibleId) return state.map((s) => ({ ...s, flex: undefined }));

    return state.map((s) => (s.colId === lastVisibleId ? { ...s, flex: 1 } : { ...s, flex: undefined }));
};

export const redrawModifiedRow = (gridApi: GridApi) => {
    const rowToRefresh = useDashboardTableStore.getState().rowToRefresh;
    if (rowToRefresh) {
        const row = gridApi.getRowNode(rowToRefresh);

        if (row) {
            row.setRowHeight(getRowHeightForModifiedRow(row.data));
            gridApi.onRowHeightChanged();
            gridApi.refreshCells({ rowNodes: [row], force: true });

            if (useDashboardFilterStore.getState().displayOptions.showSummaryRows) {
                const summaryNode = gridApi.getDisplayedRowAtIndex(0);
                redrawSummaryRow(gridApi, summaryNode);
            }
            useDashboardTableStore.setState({ rowToRefresh: undefined });
        }
    }
};

const getRowHeightForModifiedRow = (data: any): number => {
    const lengths = [
        data.itemDemographics.length,
        data.itemEquipments?.length ?? 0,
        data.addOns?.length ?? 0,
        data.purchaseNotes?.length ?? 0,
    ];
    return calculateDynamicHeight(lengths, DEFAULT_ROW_HEIGHT);
};

const getMaxLength = (lengths: number[]) => Math.max(...lengths);

const calculateDynamicHeight = (lengths: number[], baseHeight: number, maxAllowed = 1): number => {
    const maxLength = getMaxLength(lengths);
    if (maxLength <= maxAllowed) return baseHeight;
    return baseHeight + (maxLength - 1) * 20;
};

export const getRowHeight = (params: RowHeightParams) => {
    const { node, data } = params;
    // Summary Row
    if (node.id?.startsWith("summary")) {
        return node.expanded ? getSummaryHeight(data) : DEFAULT_ROW_HEIGHT;
    }
    // Organizer Row
    if (data.isOrganizer && data.itemDemographics) {
        const lengths = [
            data.itemDemographics.length,
            data.itemEquipments?.length ?? 0,
            data.addOns?.length ?? 0,
            data.purchaseNotes?.length ?? 0,
        ];
        return calculateDynamicHeight(lengths, DEFAULT_ROW_HEIGHT);
    }
    // Roster Section Row ("Attendees" / "Equipment" sub-summary group headers)
    if (data.rosterSection) {
        const lengths = [data.itemDemographics?.length ?? 0, data.itemEquipments?.length ?? 0];
        return calculateDynamicHeight(lengths, DEFAULT_ROW_HEIGHT);
    }
    // Guest Row
    if (data.guest) {
        return calculateDynamicHeight(
            [data.guest.formResponse?.fields?.filter((field: FieldResponse) => isAddonField(field.type)).length ?? 0],
            DEFAULT_ROW_HEIGHT,
        );
    }
    return DEFAULT_ROW_HEIGHT;
};

export const getSummaryHeight = (data: any): number => {
    const lengths = [
        data.demographics.length,
        data.equipments?.length ?? 0,
        data.addOns.length,
        Object.entries(data.checkInStatus).filter(([k, v]) => k !== "arrived" && (v as number) > 0).length + 1,
    ];
    return getMaxLength(lengths) <= 2 ? SUMMARY_ROW_HEIGHT : calculateDynamicHeight(lengths, DEFAULT_ROW_HEIGHT, 2);
};

export const redrawSummaryRow = (api: GridApi, summaryNode?: IRowNode) => {
    if (summaryNode) {
        if (summaryNode.data.expanded) {
            summaryNode.setExpanded(true);
        }
        summaryNode.setRowHeight(summaryNode.expanded ? getSummaryHeight(summaryNode.data) : DEFAULT_ROW_HEIGHT);
        api.onRowHeightChanged();
        api.refreshCells({ rowNodes: [summaryNode], force: true });
    }
};
